import { Box, FormControl, Input, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import WrappedButton from "../WrappedButton";

// Interface for packing list data
export interface PackingList {
	BuildingName: string;
	InvoiceNumber: string;
	SerialNumber: string;
	Mechanic: string;
	InvoiceHeaderID: string;
	PackingListID: string;
}

/**
 * Component for displaying approved packing lists
 * Shows a table of packing lists that have been signed/approved
 */
export default function ApprovedPackingLists() {
	const [data, setData] = React.useState<Array<PackingList>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");

	// Fetch approved packing lists on component mount
	React.useEffect(() => {
		getData();
	}, []);

	/**
	 * Fetches approved packing lists from the API
	 */
	const getData = () => {
		fetch("PackingList/GetPackingLists", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ approved: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							BuildingName: y.building_name,
							InvoiceNumber: y.invoice_number,
							SerialNumber: y.serial_number,
							Mechanic: y.mechanic_name,
							InvoiceHeaderID: y.invoice_header_id,
							PackingListID: y.packing_list_id,
						} as PackingList;
					})
				);
			});
	};

	/**
	 * Opens the PDF for a given packing list in a new tab
	 */
	const openPdf = (inv: PackingList) => {
		fetch("Proposal/GetPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: inv.InvoiceNumber,
				isPackingList: true,
				invoiceHeaderId: inv.InvoiceHeaderID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			});
	};

	/**
	 * Opens the signed PDF for a given packing list in a new tab
	 */
	const openSignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetSignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: inv.PackingListID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			});
	};

	// Calculate pagination values
	const maxPages = Math.ceil((data ?? []).length / 25);
	const thisRows = data
		?.filter(
			(x) =>
				Util.isNullOrEmpty(globalFilter) ||
				x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
				x.InvoiceNumber.toString().includes(globalFilter)
		)
		.slice(pageNumber * 25, (pageNumber + 1) * 25);

	return (
		<Box sx={{ overflowY: "auto", padding: 2 }}>
			<div style={{ display: "flex" }}>
				<div style={{ flex: 1 }}>
					<Typography level="title-lg">Assigned Work Orders</Typography>
				</div>
				<FormControl sx={{ marginRight: 1 }}>
					<Input
						name="Search"
						placeholder="Global Search"
						onChange={(event) => {
							setGlobalFilter(event.target.value);
						}}
						value={globalFilter}
					/>
				</FormControl>
			</div>
			<Table stripe={"odd"} hoverRow>
				<thead>
					<tr>
						<th>Building Name</th>
						<th>Estimate #</th>
						<th>Serial #</th>
						<th>Mechanic</th>
						<th>Open PDF</th>
						<th>Open Signed PDF</th>
					</tr>
				</thead>
				<tbody>
					{thisRows?.map((x, idx) => {
						return (
							<tr key={idx} style={{ cursor: "pointer" }}>
								<td>{x.BuildingName}</td>
								<td>{x.InvoiceNumber}</td>
								<td>{x.SerialNumber}</td>
								<td>{x.Mechanic}</td>
								<td>
									<WrappedButton
										onClick={() => {
											openPdf(x);
										}}>
										Open PDF
									</WrappedButton>
								</td>
								<td>
									<WrappedButton
										onClick={() => {
											openSignedPdf(x);
										}}>
										Open Signed PDF
									</WrappedButton>
								</td>
							</tr>
						);
					})}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={6}>
							<div style={{ float: "right" }}>
								<span>
									<KeyboardArrowLeftIcon
										onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)}
										sx={{ verticalAlign: "middle", cursor: "pointer" }}
									/>{" "}
									{`Page ${pageNumber + 1} of ${maxPages}`}{" "}
									<KeyboardArrowRightIcon
										onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)}
										sx={{ verticalAlign: "middle", cursor: "pointer" }}
									/>
								</span>
							</div>
						</td>
					</tr>
				</tfoot>
			</Table>
		</Box>
	);
}
